import { useTranslation } from 'react-i18next';

import type {
  BookingEventDetail,
  BookingEventRegistration,
  MutationResponse,
} from '@/types';
import type { TFunction } from 'i18next';

import { Form } from '@/components/Form';

import { useAppForm } from '@/hooks';

import {
  notifyBookingSuccess,
  requiredFieldIds,
  toFormString,
} from '@/helpers';

import { createBookingEventRegistrationEditSchema } from '@/types/schemas';

import { BookingDialogBody } from '../BookingDialogBody';
import { BookingDialogFooter } from '../BookingDialogFooter';
import { BookingPersonName } from '../BookingPersonName';
import { ServiceCustomFields } from '../ServiceCustomFields';

import {
  buildRegistrationsUrl,
  registeredIn,
  registrationCustomFieldValues,
  updateEventRegistrationPayload,
} from './utils';

type EditRegistrationFormProps = {
  event: BookingEventDetail;
  registration: BookingEventRegistration;
  eventsUrl: string;
  onClose: () => void;
  onSuccess: () => void;
};

export const EditRegistrationForm = (props: EditRegistrationFormProps) => {
  const {
    event,
    registration,
    eventsUrl,
    onClose,
    onSuccess: propOnSuccess,
  } = props;
  const { t } = useTranslation();

  const customFields =
    registeredIn(event, registration.event_wave_id)?.service
      .service_custom_fields ?? [];

  const requiredCustomFieldIds = requiredFieldIds(customFields);

  const onSuccess = (response: MutationResponse) => {
    notifyBookingSuccess(response);
    propOnSuccess();
    onClose();
  };

  const { form, formFields, onSubmit, isPending } = useAppForm({
    schema: (translate: TFunction) =>
      createBookingEventRegistrationEditSchema(
        translate,
        requiredCustomFieldIds,
      ),
    defaultValues: {
      staff_comment: toFormString(registration.staff_comment),
      custom_field_values: registrationCustomFieldValues(registration),
    },
    transform: updateEventRegistrationPayload,
    api_url: buildRegistrationsUrl(eventsUrl, event.id),
    entityId: registration.id,
    redirectUrl: '',
    onSuccess,
  });

  return (
    <>
      <BookingDialogBody>
        <p className="text-sm font-medium">{t('Edit participant')}</p>

        <BookingPersonName
          client={registration.client}
          guestInfo={registration.guest_info}
          className="text-sm"
        />

        <Form
          form={form}
          onSubmit={onSubmit}
        >
          <ServiceCustomFields fields={customFields} />

          <formFields.Textarea
            name="staff_comment"
            label={t('Comment')}
          />
        </Form>
      </BookingDialogBody>

      <BookingDialogFooter
        submitLabel={t('Save')}
        isPending={isPending}
        onClose={onClose}
        onSubmit={onSubmit}
      />
    </>
  );
};
